import { io, type Socket } from 'socket.io-client';

import { createLogger } from '@/logger';
import { BaseLiveClient } from '@/transport/live/BaseLiveClient';
import type { LiveClientConfig } from '@/transport/live/LiveClient';
import type { RecorderInboundMessage } from '@/transport/live/messages';

const log = createLogger('SocketIoLiveClient');

const LIVE_EVENT = 'live';

/**
 * `socket.io` recorder client. Wire vocabulary is identical to `WsLiveClient`
 * — same auth handshake (`{type:'auth', apiKey}` on the `live` event), same
 * batch shape (`{events:[...]}`), same ack semantics (`{type:'ack'}`). The
 * only difference is the framing protocol underneath.
 */
export class SocketIoLiveClient extends BaseLiveClient {
  readonly name = 'SocketIO';

  private socket: Socket | null = null;

  constructor(config: LiveClientConfig) {
    super(config);
  }

  isReady(): boolean {
    return !!this.socket?.connected && this.pendingAck === null;
  }

  sendRaw(msg: Record<string, unknown>): void {
    if (!this.socket || !this.socket.connected) return;
    try {
      this.socket.emit(LIVE_EVENT, msg);
    } catch (err) {
      log.warn('sendRaw failed', err as Error);
    }
  }

  send(events: unknown[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        reject(new Error('[SocketIoLiveClient] Socket is not open'));
        return;
      }
      if (this.pendingAck) {
        reject(new Error('[SocketIoLiveClient] A batch is already awaiting acknowledgement'));
        return;
      }

      try {
        this.socket.emit(LIVE_EVENT, { events });
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }

      const timer = setTimeout(() => {
        this.rejectPendingAck(new Error('[SocketIoLiveClient] Timed out waiting for server ack'));
        try {
          this.socket?.disconnect();
        } catch {
          /* ignore */
        }
      }, this.ackTimeoutMs);

      this.pendingAck = { resolve, reject, timer };
    });
  }

  dispose(): void {
    this.disposed = true;
    this.rejectPendingAck(new Error('[SocketIoLiveClient] Transport disposed'));
    this.socket?.disconnect();
    this.socket = null;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.disposed) {
        reject(new Error('[SocketIoLiveClient] Transport has been disposed'));
        return;
      }

      const socket = io(this.url, {
        query: { session: this.sessionId, role: 'recorder' },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.baseReconnectDelayMs,
        timeout: this.openTimeoutMs,
        forceNew: true,
      });

      let settled = false;
      const settle = (fn: () => void): void => {
        if (settled) return;
        settled = true;
        clearTimeout(openTimeout);
        fn();
      };

      const openTimeout = setTimeout(() => {
        settle(() => {
          socket.disconnect();
          reject(new Error('[SocketIoLiveClient] Connection timed out'));
        });
      }, this.openTimeoutMs);

      socket.on('connect', () => {
        settle(() => {
          this.socket = socket;
          socket.emit(LIVE_EVENT, { type: 'auth', apiKey: this.apiKey });
          log.debug('socket.io connected');
          resolve();
        });
      });

      socket.on(LIVE_EVENT, (msg: Partial<RecorderInboundMessage>) => {
        if (!msg || typeof msg.type !== 'string') return;
        if (msg.type === 'ack') this.resolvePendingAck();
        else if (msg.type === 'control:cursor') this.cursorCallback?.(msg.x!, msg.y!);
        else if (msg.type === 'control:click') this.clickCallback?.(msg.x!, msg.y!);
        else if (msg.type === 'control:focus') this.focusCallback?.(msg.x!, msg.y!);
        else this.messageCallback?.(msg as { type: string; [k: string]: unknown });
      });

      socket.on('live:close', (info: { code?: number; reason?: string }) => {
        log.warn(`socket.io close signal: ${info.reason ?? ''}`);
        this.rejectPendingAck(
          new Error(`[SocketIoLiveClient] Closed by server: ${info.reason ?? ''}`)
        );
      });

      socket.on('connect_error', (err) => {
        settle(() => reject(err instanceof Error ? err : new Error(String(err))));
      });

      socket.on('disconnect', () => {
        if (this.socket === socket) this.socket = null;
        this.rejectPendingAck(new Error('[SocketIoLiveClient] Socket disconnected'));
      });
    });
  }
}
